import { Button, cn } from '@wundo/ui';
import type { ReviewSeverity } from '@/lib/conversationReviews.ts';
import { SEVERITY_LABELS, SEVERITY_MARK_CLASS, SEVERITY_ORDER } from './severity.ts';

export interface SeverityFilterChipsProps {
  selected: ReviewSeverity[];
  /** How many reviews of each rung the other filters leave. Absent while loading. */
  counts?: Record<ReviewSeverity, number> | null;
  onToggle: (severity: ReviewSeverity) => void;
}

/**
 * The severities as toggles, each carrying its own share of the list.
 *
 * A picked chip keeps its dot rather than filling with navy: the dot is the only
 * thing that says which rung it is, and half the palette disappears on a dark
 * fill. The ring and the grey fill carry the pressed state instead.
 */
export function SeverityFilterChips({
  selected,
  counts,
  onToggle,
}: SeverityFilterChipsProps) {
  return (
    <div className='flex flex-wrap items-center gap-2'>
      {SEVERITY_ORDER.map((severity) => {
        const active = selected.includes(severity);

        return (
          <Button
            key={severity}
            type='button'
            size='sm'
            variant={active ? 'quiet' : 'ghost'}
            aria-pressed={active}
            className={active ? 'ring-2 ring-ink' : 'ring-1 ring-line'}
            onClick={() => onToggle(severity)}
          >
            <span
              aria-hidden='true'
              className={cn('size-1.5 rounded-full', SEVERITY_MARK_CLASS[severity])}
            />
            {SEVERITY_LABELS[severity]}
            {counts && (
              <span className='font-semibold tabular-nums text-ink-muted'>
                {counts[severity]}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
